import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Divider,
    Alert,
    InputAdornment,
    Collapse,
    useTheme,
    alpha
} from '@mui/material';
import { usePermissions } from '../../../../utils/permissions';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Science as ScienceIcon,
    Speed as SpeedIcon,
    Thermostat as ThermostatIcon,
    Schedule as ScheduleIcon,
    SwapHoriz as ConvertIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Settings as SettingsIcon,
    Close as CloseIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import {
    convertUnits,
    formatUnitDisplay,
    areUnitsCompatible,
    getAllUnits,
    getUnitType,
    WEIGHT_UNITS,
    VOLUME_UNITS
} from '../../../../utils/unitConversions';

// Component to display multiple lots for an ingredient
const IngredientLotsCards = ({ ingredient, lotNumber, theme, alpha, batchIngredients = [] }) => {
    const [ingredientLots, setIngredientLots] = useState([]);
    
    useEffect(() => {
        const loadLots = async () => {
            try {
                const { data, error } = await supabase
                    .from('batch_log_ingredient_lots')
                    .select('*')
                    .eq('batch_log_ingredient_id', ingredient.id);
                
                if (!error && data && data.length > 0) {
                    setIngredientLots(data);
                } else if (lotNumber) {
                    // Fallback to single lot display
                    setIngredientLots([{ lot_number: lotNumber, batch_number: null }]);
                }
            } catch (err) {
                console.error('Error loading lots:', err);
                if (lotNumber) {
                    setIngredientLots([{ lot_number: lotNumber, batch_number: null }]);
                }
            }
        };
        
        if (ingredient.id) {
            loadLots();
        }
    }, [ingredient.id, lotNumber]);
    
    return (
        <>
            {ingredientLots.map((lot, lotIndex) => (
                <Grid item xs={12} sm={6} md={4} key={lotIndex}>
                    <Card sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.success.main, 0.05),
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                        }
                    }}>
                        <Typography variant="subtitle2" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.success.main,
                            mb: 1
                        }}>
                            {lot.lot_number ? `Lot ${lot.lot_number}` : `Batch ${lot.batch_number}`}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                            color: theme.palette.text.secondary,
                            mb: 0.5
                        }}>
                            Quantity: {lot.quantity_used} {(() => {
                                // Get the raw material unit from the batch ingredient
                                const batchIngredient = batchIngredients.find(bi => bi.id === ingredient.batch_ingredient_id);
                                const rawMaterialUnit = batchIngredient?.raw_materials?.primary_unit_measure;
                                return rawMaterialUnit || lot.unit_used || 'units';
                            })()}
                        </Typography>
                        {lot.location_name && (
                            <Typography variant="caption" sx={{ 
                                color: theme.palette.text.secondary,
                                display: 'block'
                            }}>
                                Location: {lot.location_name}
                            </Typography>
                        )}
                        {lot.expiry_date && (
                            <Typography variant="caption" sx={{ 
                                color: theme.palette.text.secondary,
                                display: 'block'
                            }}>
                                Expires: {new Date(lot.expiry_date).toLocaleDateString()}
                            </Typography>
                        )}
                    </Card>
                </Grid>
            ))}
        </>
    );
};

export default function IngredientsLog({ 
    ingredients, 
    setIngredients, 
    batchLogId, 
    batchData, 
    createBatchLog,
    batchIngredients = [],
    showSnackbar,
    batchLog = null,
    batchNumber,
    lotNumber,
    batchTimeFormat = '12hour'
}) {
    const theme = useTheme();
    const { canAccessPage, canEditPage, canDeletePage } = usePermissions();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingIngredient, setEditingIngredient] = useState(null);
    const [unitConversionWarning, setUnitConversionWarning] = useState('');
    const [expandedIngredients, setExpandedIngredients] = useState(new Set());
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [ingredientToDelete, setIngredientToDelete] = useState(null);
    
    // Batch ingredient selection dialog state
    const [batchIngredientSelectionDialogOpen, setBatchIngredientSelectionDialogOpen] = useState(false);
    const [batchIngredientSearchTerm, setBatchIngredientSearchTerm] = useState('');
    
    // Multiple lots state for current ingredient
    const [ingredientLots, setIngredientLots] = useState([]);
    
    const [formData, setFormData] = useState({
        batch_ingredient_id: '',
        ingredient_name: '',
        ingredient_id: null,
        lot_number: '',
        supplier_name: '',
        planned_quantity: '',
        actual_quantity: '',
        unit: '',
        time_of_addition: dayjs(),
        added_by_operator: '',
        addition_order: ingredients.length + 1,
        mixer_speed_rpm: '',
        mixer_setting: '',
        heating_temperature: '',
        heating_temperature_unit: 'F',
        mixing_duration_minutes: '',
        ph_reading: '',
        viscosity_reading: '',
        visual_assessment: '',
        notes: ''
    });

    // Get available batch ingredients that haven't been logged yet
    const getAvailableBatchIngredients = () => {
        const loggedIngredientIds = ingredients.map(ing => ing.batch_ingredient_id).filter(Boolean);
        return batchIngredients.filter(bi => !loggedIngredientIds.includes(bi.id));
    };

    // Batch ingredient selection dialog functions
    const handleOpenBatchIngredientSelection = () => {
        setBatchIngredientSearchTerm('');
        setBatchIngredientSelectionDialogOpen(true);
    };

    const handleCloseBatchIngredientSelection = () => {
        setBatchIngredientSelectionDialogOpen(false);
        setBatchIngredientSearchTerm('');
    };

    const handleSelectBatchIngredient = (batchIngredient) => {
        handleBatchIngredientSelect(batchIngredient.id);
        handleCloseBatchIngredientSelection();
    };

    const getFilteredBatchIngredients = () => {
        const available = getAvailableBatchIngredients();
        if (!batchIngredientSearchTerm.trim()) return available;
        
        const searchTerm = batchIngredientSearchTerm.toLowerCase();
        return available.filter(bi => 
            bi.raw_materials?.name?.toLowerCase().includes(searchTerm) ||
            bi.supplier_lot_number?.toLowerCase().includes(searchTerm) ||
            bi.supplier_batch_number?.toLowerCase().includes(searchTerm) ||
            bi.raw_materials?.supplier?.name?.toLowerCase().includes(searchTerm) ||
            bi.usage_notes?.toLowerCase().includes(searchTerm)
        );
    };

    const handleAddIngredient = () => {
        setEditingIngredient(null);
        setIngredientLots([]); // Clear lots for new ingredient
        const availableIngredients = getAvailableBatchIngredients();
        const nextOrder = Math.max(...ingredients.map(i => i.addition_order || 0), 0) + 1;
        
        setFormData({
            batch_ingredient_id: '',
            ingredient_name: '',
            ingredient_id: null,
            lot_number: '',
            supplier_name: '',
            planned_quantity: '',
            actual_quantity: '',
            unit: batchData?.planned_unit_of_measure || 'g',
            time_of_addition: dayjs(),
            added_by_operator: batchLog?.operator_name || '',
            addition_order: nextOrder,
            mixer_speed_rpm: '',
            mixer_setting: '',
            heating_temperature: '',
            heating_temperature_unit: 'F',
            mixing_duration_minutes: '',
            ph_reading: '',
            viscosity_reading: '',
            visual_assessment: '',
            notes: ''
        });
        setUnitConversionWarning('');
        setOpenDialog(true);
    };

    const handleEditIngredient = async (ingredient) => {
        setEditingIngredient(ingredient);
        
        // Load existing lots for this ingredient
        try {
            const { data: lotsData, error: lotsError } = await supabase
                .from('batch_log_ingredient_lots')
                .select('*')
                .eq('batch_log_ingredient_id', ingredient.id)
                .order('created_at');

            if (lotsError) {
                console.error('Error loading existing ingredient lots:', lotsError);
                setIngredientLots([]);
            } else {
                setIngredientLots(lotsData || []);
            }
        } catch (error) {
            console.error('Error loading existing ingredient lots:', error);
            setIngredientLots([]);
        }

        setFormData({
            batch_ingredient_id: ingredient.batch_ingredient_id || '',
            ingredient_name: ingredient.ingredient_name || '',
            ingredient_id: ingredient.ingredient_id || null,
            lot_number: ingredient.lot_number || '',
            supplier_name: ingredient.supplier_name || '',
            planned_quantity: ingredient.planned_quantity || '',
            actual_quantity: ingredient.actual_quantity || '',
            unit: ingredient.unit || batchData?.planned_unit_of_measure || 'g',
            time_of_addition: ingredient.time_of_addition ? dayjs(ingredient.time_of_addition) : dayjs(),
            added_by_operator: ingredient.added_by_operator || batchLog?.operator_name || '',
            addition_order: ingredient.addition_order || 1,
            mixer_speed_rpm: ingredient.mixer_speed_rpm || '',
            mixer_setting: ingredient.mixer_setting || '',
            heating_temperature: ingredient.heating_temperature || '',
            heating_temperature_unit: ingredient.heating_temperature_unit || 'F',
            mixing_duration_minutes: ingredient.mixing_duration_minutes || '',
            ph_reading: ingredient.ph_reading || '',
            viscosity_reading: ingredient.viscosity_reading || '',
            visual_assessment: ingredient.visual_assessment || '',
            notes: ingredient.notes || ''
        });
        setUnitConversionWarning('');
        setOpenDialog(true);
    };

    const handleBatchIngredientSelect = async (batchIngredientId) => {
        const batchIngredient = batchIngredients.find(bi => bi.id === batchIngredientId);
        if (!batchIngredient) return;

        // Load lot selections from batch_ingredient_lots table
        try {
            const { data: lotsData, error: lotsError } = await supabase
                .from('batch_ingredient_lots')
                .select('*')
                .eq('batch_ingredient_id', batchIngredientId)
                .order('created_at');

            if (lotsError) {
                console.error('Error loading ingredient lots:', lotsError);
                setIngredientLots([]);
            } else {
                setIngredientLots(lotsData || []);
            }
        } catch (error) {
            console.error('Error loading ingredient lots:', error);
            setIngredientLots([]);
        }

        // Auto-populate ALL data from batch ingredient
        setFormData(prev => ({
            ...prev,
            batch_ingredient_id: batchIngredientId,
            ingredient_name: batchIngredient.raw_materials?.name || '',
            ingredient_id: batchIngredient.raw_material_id || null,
            lot_number: '', // Clear single lot field since we now use multiple lots
            supplier_name: batchIngredient.raw_materials?.supplier?.name || '',
            planned_quantity: batchIngredient.planned_amount?.toString() || '',
            actual_quantity: batchIngredient.actual_amount?.toString() || batchIngredient.planned_amount?.toString() || '',
            unit: batchIngredient.actual_unit || batchIngredient.planned_unit || batchData?.planned_unit_of_measure || 'g',
            added_by_operator: batchLog?.operator_name || '',
            // Also populate any notes from batch ingredient
            notes: batchIngredient.usage_notes || ''
        }));
        setUnitConversionWarning('');
    };

    const handleUnitChange = (newUnit) => {
        const oldUnit = formData.unit;
        const amount = parseFloat(formData.actual_quantity);
        
        if (oldUnit !== newUnit && amount > 0) {
            // Check if units are compatible
            const batchIngredient = batchIngredients.find(bi => bi.id === formData.batch_ingredient_id);
            const density = batchIngredient?.raw_materials?.density_g_ml;
            
            if (areUnitsCompatible(oldUnit, newUnit, !!density)) {
                try {
                    // Convert the amount to new unit
                    const convertedAmount = convertUnits(amount, oldUnit, newUnit, density);
                    setFormData({
                        ...formData,
                        actual_quantity: convertedAmount.toFixed(3),
                        unit: newUnit
                    });
                    setUnitConversionWarning(`Converted ${amount} ${oldUnit} to ${convertedAmount.toFixed(3)} ${newUnit}`);
                } catch (error) {
                    setUnitConversionWarning(`Warning: Could not convert from ${oldUnit} to ${newUnit}. Please verify the amount.`);
                    setFormData({
                        ...formData,
                        unit: newUnit
                    });
                }
            } else {
                setUnitConversionWarning(`Warning: ${oldUnit} and ${newUnit} are not compatible. Please verify the amount.`);
                setFormData({
                    ...formData,
                    unit: newUnit
                });
            }
        } else {
            setFormData({
                ...formData,
                unit: newUnit
            });
            setUnitConversionWarning('');
        }
    };

    const convertTemperature = (temp, fromUnit, toUnit) => {
        if (fromUnit === toUnit) return temp;
        if (fromUnit === 'F' && toUnit === 'C') {
            return (temp - 32) * 5/9;
        }
        if (fromUnit === 'C' && toUnit === 'F') {
            return (temp * 9/5) + 32;
        }
        return temp;
    };

    const handleTemperatureUnitChange = (newUnit) => {
        const currentTemp = parseFloat(formData.heating_temperature);
        const oldUnit = formData.heating_temperature_unit;
        
        if (oldUnit !== newUnit && currentTemp > 0) {
            const convertedTemp = convertTemperature(currentTemp, oldUnit, newUnit);
            setFormData({
                ...formData,
                heating_temperature: convertedTemp.toFixed(1),
                heating_temperature_unit: newUnit
            });
            setUnitConversionWarning(`Temperature converted from ${currentTemp}°${oldUnit} to ${convertedTemp.toFixed(1)}°${newUnit}`);
        } else {
            setFormData({
                ...formData,
                heating_temperature_unit: newUnit
            });
        }
    };

    const handleSaveIngredient = async () => {
        try {
            // Validation — only actual quantity is required
            if (!formData.actual_quantity) {
                showSnackbar?.('Actual Quantity is required', 'error');
                return;
            }

            const logId = await createBatchLog();
            if (!logId) {
                console.error('Failed to create or get batch log ID for ingredient');
                showSnackbar?.('Failed to create batch log', 'error');
                return;
            }

            const saveData = {
                batch_log_id: logId,
                batch_ingredient_id: formData.batch_ingredient_id || null,
                ingredient_name: formData.ingredient_name,
                lot_number: formData.lot_number || null,
                supplier_name: formData.supplier_name || null,
                time_of_addition: formData.time_of_addition.toISOString(),
                added_by_operator: formData.added_by_operator || null,
                mixer_setting: formData.mixer_setting || null,
                visual_assessment: formData.visual_assessment || null,
                notes: formData.notes || null,
                unit: formData.unit || null,
                // Convert empty strings to null for numeric fields
                planned_quantity: formData.planned_quantity ? parseFloat(formData.planned_quantity) : null,
                actual_quantity: formData.actual_quantity ? parseFloat(formData.actual_quantity) : null,
                addition_order: formData.addition_order ? parseInt(formData.addition_order) : null,
                mixer_speed_rpm: formData.mixer_speed_rpm ? parseInt(formData.mixer_speed_rpm) : null,
                mixing_duration_minutes: formData.mixing_duration_minutes ? parseInt(formData.mixing_duration_minutes) : null,
                ph_reading: formData.ph_reading ? parseFloat(formData.ph_reading) : null,
                viscosity_reading: formData.viscosity_reading ? parseFloat(formData.viscosity_reading) : null,
                // Convert temperature to Celsius for storage if needed
                heating_temperature: formData.heating_temperature ? 
                    (formData.heating_temperature_unit === 'F' ? 
                        convertTemperature(parseFloat(formData.heating_temperature), 'F', 'C') : 
                        parseFloat(formData.heating_temperature)) : null,
                heating_temperature_unit: 'C' // Always store in Celsius
            };

            let result;
            if (editingIngredient) {
                const { data, error } = await supabase
                    .from('batch_log_ingredients')
                    .update(saveData)
                    .eq('id', editingIngredient.id)
                    .select()
                    .single();
                if (error) {
                    console.error('Error updating ingredient:', error);
                    showSnackbar?.('Error updating ingredient: ' + error.message, 'error');
                    return;
                }
                result = data;
                
                setIngredients(prev => 
                    prev.map(ing => ing.id === editingIngredient.id ? result : ing)
                );
                showSnackbar?.('Ingredient updated successfully', 'success');
            } else {
                const { data, error } = await supabase
                    .from('batch_log_ingredients')
                    .insert(saveData)
                    .select()
                    .single();
                if (error) {
                    console.error('Error inserting ingredient:', error);
                    showSnackbar?.('Error adding ingredient: ' + error.message, 'error');
                    return;
                }
                result = data;
                
                setIngredients(prev => [...prev, result]);
                showSnackbar?.('Ingredient added successfully', 'success');
            }

            // Save multiple lots to batch_log_ingredient_lots table
            if (ingredientLots.length > 0) {
                try {
                    // First, delete existing lot records if editing
                    if (editingIngredient) {
                        await supabase
                            .from('batch_log_ingredient_lots')
                            .delete()
                            .eq('batch_log_ingredient_id', result.id);
                    }

                    // Insert new lot records
                    const lotRecords = ingredientLots.map(lot => ({
                        batch_log_ingredient_id: result.id,
                        inventory_item_id: lot.inventory_item_id,
                        quantity_used: lot.quantity_used,
                        unit_used: lot.unit_used,
                        lot_number: lot.lot_number,
                        batch_number: lot.batch_number,
                        expiry_date: lot.expiry_date,
                        manufactured_date: lot.manufactured_date,
                        location_name: lot.location_name,
                        notes: lot.notes || null
                    }));

                    const { error: lotsError } = await supabase
                        .from('batch_log_ingredient_lots')
                        .insert(lotRecords);

                    if (lotsError) {
                        console.error('Error saving ingredient lots:', lotsError);
                        showSnackbar?.('Ingredient saved but error saving lot details: ' + lotsError.message, 'warning');
                    }
                } catch (lotError) {
                    console.error('Error saving ingredient lots:', lotError);
                    showSnackbar?.('Ingredient saved but error saving lot details', 'warning');
                }
            }

            setOpenDialog(false);
            setUnitConversionWarning('');
            setIngredientLots([]); // Clear lots for next ingredient
        } catch (error) {
            console.error('Error saving ingredient:', error);
            showSnackbar?.('Error saving ingredient: ' + error.message, 'error');
        }
    };

    const handleDeleteIngredient = async (ingredientId) => {
        try {
            // First, remove references to this ingredient from QC checks
            const { error: qcUpdateError } = await supabase
                .from('batch_log_qc_checks')
                .update({ ingredient_log_id: null })
                .eq('ingredient_log_id', ingredientId);

            if (qcUpdateError) {
                console.error('Error updating QC checks:', qcUpdateError);
                showSnackbar?.('Error updating related QC checks: ' + qcUpdateError.message, 'error');
                return;
            }

            // Now delete the ingredient
            const { error } = await supabase
                .from('batch_log_ingredients')
                .delete()
                .eq('id', ingredientId);

            if (error) throw error;

            setIngredients(prev => prev.filter(ing => ing.id !== ingredientId));
            showSnackbar?.('Ingredient deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting ingredient:', error);
            showSnackbar?.('Error deleting ingredient: ' + error.message, 'error');
        }
    };

    const confirmDelete = (ingredient) => {
        setIngredientToDelete(ingredient);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = () => {
        if (ingredientToDelete) {
            handleDeleteIngredient(ingredientToDelete.id);
        }
        setDeleteConfirmOpen(false);
        setIngredientToDelete(null);
    };

    const getVarianceColor = (planned, actual) => {
        if (!planned || !actual) return 'default';
        const variance = Math.abs((actual - planned) / planned) * 100;
        if (variance <= 2) return 'success';
        if (variance <= 5) return 'warning';
        return 'error';
    };

    const getVariancePercentage = (planned, actual) => {
        if (!planned || !actual) return 'N/A';
        const variance = ((actual - planned) / planned) * 100;
        return `${variance > 0 ? '+' : ''}${variance.toFixed(2)}%`;
    };

    const formatTemperatureDisplay = (temp, unit = 'C') => {
        if (!temp) return '—';
        // Convert from stored Celsius to display unit
        const displayTemp = unit === 'F' ? convertTemperature(temp, 'C', 'F') : temp;
        return `${displayTemp.toFixed(1)}°${unit}`;
    };

    // Calculate raw material conversion amounts (same logic as other components)
    const calculateRawMaterialAmountNeeded = (amount, formulationUnit, rawMaterial) => {
        if (!rawMaterial || !amount) return null;
        
        const materialDensity = rawMaterial.density_g_ml || 1.0;
        const rawMaterialUnit = rawMaterial.primary_unit_measure;
        
        // If same units, no conversion needed
        if (!rawMaterialUnit || rawMaterialUnit === formulationUnit) {
            return null;
        }

        try {
            // Convert formulation amount to raw material units using unitConversions.js with density
            const rawMaterialAmountNeeded = convertUnits(
                amount,
                formulationUnit,
                rawMaterialUnit,
                materialDensity
            );

            return {
                amount: rawMaterialAmountNeeded.toFixed(3),
                unit: rawMaterialUnit,
                density: materialDensity.toFixed(3)
            };
        } catch (error) {
            console.warn(`Raw material unit conversion failed for ${rawMaterial?.name}: ${error.message}`);
            return null;
        }
    };

    const availableBatchIngredients = getAvailableBatchIngredients();
    const canEdit = canEditPage('batch_logs_ingredients');
    const canDelete = canDeletePage('batch_logs_ingredients');

    const toggleIngredientExpansion = (ingredientId) => {
        setExpandedIngredients(prev => {
            const newSet = new Set(prev);
            if (newSet.has(ingredientId)) {
                newSet.delete(ingredientId);
            } else {
                newSet.add(ingredientId);
            }
            return newSet;
        });
    };

    // Permission check for batch logs ingredients
    if (!canAccessPage('batch_logs_ingredients')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                px: 3
            }}>
                <Card sx={{
                    p: 8,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 600,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 6,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        mb: 3, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2.5rem',
                        letterSpacing: '-0.8px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 4,
                        fontWeight: 500,
                        fontSize: '1.2rem'
                    }}>
                        Ingredients Log Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        maxWidth: 450,
                        mx: 'auto'
                    }}>
                        You don't currently have the necessary permissions to access the Ingredients Log. 
                        Contact your system administrator to request access or verify your role permissions.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Grid container spacing={4}>
                    {/* Header */}
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ 
                                            p: 1.5, 
                                            borderRadius: 2, 
                                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                            backdropFilter: 'blur(10px)',
                                            mr: 2
                                        }}>
                                            <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                                Stage-by-Stage Ingredient Log
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Track ingredients, quantities, and process parameters for each addition stage
                                                {availableBatchIngredients.length > 0 && (
                                                    <> • {availableBatchIngredients.length} ingredients available to log</>
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddIngredient}
                                        size="large"
                                        disabled={!canEdit}
                                        sx={{
                                            borderRadius: 3,
                                            px: 4,
                                            py: 1.5,
                                            background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` : theme.palette.grey[400],
                                            color: canEdit ? 'white' : theme.palette.grey[600],
                                            fontWeight: 500,
                                            boxShadow: canEdit ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                                            '&:hover': {
                                                background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` : theme.palette.grey[400],
                                                transform: canEdit ? 'translateY(-1px)' : 'none',
                                                boxShadow: canEdit ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}` : 'none'
                                            },
                                            '&:disabled': {
                                                background: theme.palette.grey[400],
                                                color: theme.palette.grey[600]
                                            },
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}
                                    >
                                        {canEdit ? 'Add Ingredient' : 'Read Only - No Edit Permissions'}
                                    </Button>
                                </Box>
                                {availableBatchIngredients.length === 0 && batchIngredients.length > 0 && (
                                    <Alert 
                                        severity="info" 
                                        sx={{ 
                                            mt: 3,
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                            '& .MuiAlert-icon': {
                                                color: theme.palette.info.main
                                            }
                                        }}
                                    >
                                        All batch ingredients have been logged. Edit existing entries or add more ingredients in the Batch Ingredients tab.
                                    </Alert>
                                )}
                                {batchIngredients.length === 0 && (
                                    <Alert 
                                        severity="warning" 
                                        sx={{ 
                                            mt: 3,
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                                            '& .MuiAlert-icon': {
                                                color: theme.palette.warning.main
                                            }
                                        }}
                                    >
                                        No batch ingredients available. Generate batch ingredients first in the Batch Ingredients tab, or add ingredients manually.
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Ingredients Display */}
                    <Grid item xs={12}>
                        {ingredients.length === 0 ? (
                            <Card sx={{ 
                                borderRadius: 4,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: 'white',
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}>
                                <CardContent sx={{ textAlign: 'center', py: 8 }}>
                                    <ScienceIcon sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 3 }} />
                                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                                        No ingredients logged yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        {availableBatchIngredients.length > 0 
                                            ? 'Start by adding the first ingredient to begin the mixing log'
                                            : 'Generate batch ingredients first in the Batch Ingredients tab'
                                        }
                                    </Typography>
                                </CardContent>
                            </Card>
                        ) : (
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 3 }}>
                                    Mixing Timeline ({ingredients.length} ingredients)
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {ingredients
                                        .sort((a, b) => (a.addition_order || 0) - (b.addition_order || 0))
                                        .map((ingredient, index) => {
                                            const variance = (() => {
                                                if (!ingredient.actual_quantity || !ingredient.planned_quantity) return 0;
                                                
                                                try {
                                                    // Find the batch ingredient to get raw material info
                                                    const batchIngredient = batchIngredients.find(bi => bi.id === ingredient.batch_ingredient_id);
                                                    const rawMaterial = batchIngredient?.raw_materials;
                                                    const density = rawMaterial?.density_g_ml || 1.0;
                                                    
                                                    // If actual is in raw material units and planned is in formulation units,
                                                    // convert planned to raw material units for comparison
                                                    if (ingredient.unit !== batchIngredient?.planned_unit && rawMaterial?.primary_unit_measure === ingredient.unit) {
                                                        // Convert planned amount from formulation units to raw material units
                                                        const plannedInRawUnits = convertUnits(
                                                            ingredient.planned_quantity,
                                                            batchIngredient.planned_unit,
                                                            ingredient.unit,
                                                            density
                                                        );
                                                        
                                                        return ((ingredient.actual_quantity - plannedInRawUnits) / plannedInRawUnits) * 100;
                                                    } else {
                                                        // Convert actual to planned units for comparison
                                                        let actualInPlannedUnits = ingredient.actual_quantity;
                                                        if (ingredient.unit !== batchIngredient?.planned_unit) {
                                                            actualInPlannedUnits = convertUnits(
                                                                ingredient.actual_quantity,
                                                                ingredient.unit,
                                                                batchIngredient.planned_unit,
                                                                density
                                                            );
                                                        }
                                                        
                                                        return ((actualInPlannedUnits - ingredient.planned_quantity) / ingredient.planned_quantity) * 100;
                                                    }
                                                } catch (error) {
                                                    console.warn('Variance calculation failed in IngredientsLog:', error);
                                                    return ((ingredient.actual_quantity - ingredient.planned_quantity) / ingredient.planned_quantity) * 100;
                                                }
                                            })();
                                            const isOutOfSpec = Math.abs(variance) > 5;
                                            const isExpanded = expandedIngredients.has(ingredient.id);

                                            return (
                                                <Card key={ingredient.id} sx={{ 
                                                    borderRadius: 4,
                                                    border: `1px solid ${theme.palette.grey[400]}`,
                                                    background: 'white',
                                                    backdropFilter: 'blur(20px)',
                                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                    overflow: 'hidden',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                                    },
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                                }}>
                                                    {/* Header - Always Visible */}
                                                    <CardContent 
                                                        sx={{ 
                                                            p: 0, 
                                                            backgroundColor: 'white',
                                                            borderBottom: `1px solid ${theme.palette.grey[400]}`,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => toggleIngredientExpansion(ingredient.id)}
                                                    >
                                                        <Box sx={{ p: 4 }}>
                                                            {/* Top Row - Name and Actions */}
                                                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                        <Box sx={{
                                                                            p: 1.5,
                                                                            borderRadius: 2,
                                                                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                                                            backdropFilter: 'blur(10px)',
                                                                            mr: 3,
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            minWidth: '60px'
                                                                        }}>
                                                                            <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 500, fontSize: '0.7rem', lineHeight: 1 }}>
                                                                                STEP
                                                                            </Typography>
                                                                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main, lineHeight: 1 }}>
                                                                                {ingredient.addition_order || index + 1}
                                                                            </Typography>
                                                                        </Box>
                                                                    <Box>
                                                                        <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                                            {ingredient.ingredient_name}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                                            {ingredient.lot_number && (
                                                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                                                    LOT: {ingredient.lot_number}
                                                                </Typography>
                                                            )}
                                                            {ingredient.supplier_name && (
                                                                <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                                                                    SUPPLIER: {ingredient.supplier_name}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {canEdit && (
                                                                        <IconButton 
                                                                            size="medium" 
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleEditIngredient(ingredient);
                                                                            }}
                                                                            sx={{ 
                                                                                color: theme.palette.primary.main,
                                                                                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                                                borderRadius: 2,
                                                                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                                '&:hover': {
                                                                                    bgcolor: theme.palette.primary.main,
                                                                                    color: 'white',
                                                                                    transform: 'scale(1.05)',
                                                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                                                                }
                                                                            }}
                                                                        >
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                    )}
                                                                    {canDelete && (
                                                                        <IconButton 
                                                                            size="medium" 
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                confirmDelete(ingredient);
                                                                            }}
                                                                            sx={{ 
                                                                                color: theme.palette.error.main,
                                                                                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                                                borderRadius: 2,
                                                                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                                '&:hover': {
                                                                                    bgcolor: theme.palette.error.main,
                                                                                    color: 'white',
                                                                                    transform: 'scale(1.05)',
                                                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
                                                                                }
                                                                            }}
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    )}
                                                                    <IconButton 
                                                                        size="medium"
                                                                        sx={{ 
                                                                            color: theme.palette.primary.main,
                                                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                                                            borderRadius: 2,
                                                                            ml: 1,
                                                                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                bgcolor: theme.palette.primary.main,
                                                                                color: 'white',
                                                                                transform: 'scale(1.05)',
                                                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>

                                                            {/* Quantity and Status Row */}
                                                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                    <Box sx={{ textAlign: 'center' }}>
                                                                        {(() => {
                                                                            // Find the raw material for this ingredient
                                                                            const batchIngredient = batchIngredients.find(bi => bi.id === ingredient.batch_ingredient_id);
                                                                            const rawMaterial = batchIngredient?.raw_materials;
                                                                            
                                                                            if (ingredient.planned_quantity && rawMaterial) {
                                                                                const rawMaterialConversion = calculateRawMaterialAmountNeeded(
                                                                                    ingredient.planned_quantity,
                                                                                    ingredient.unit,
                                                                                    rawMaterial
                                                                                );
                                                                                
                                                                                if (rawMaterialConversion) {
                                                                                    return (
                                                                                        <>
                                                                                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.secondary, lineHeight: 1 }}>
                                                                                                {rawMaterialConversion.amount} {rawMaterialConversion.unit}
                                                                                            </Typography>
                                                                                            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 500, display: 'block' }}>
                                                                                                (≈{ingredient.planned_quantity} {ingredient.unit})
                                                                                            </Typography>
                                                                                        </>
                                                                                    );
                                                                                }
                                                                            }
                                                                            
                                                                            return (
                                                                                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.secondary, lineHeight: 1 }}>
                                                                                    {ingredient.planned_quantity || '—'}
                                                                                </Typography>
                                                                            );
                                                                        })()}
                                                                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 500 }}>
                                                                            PLANNED
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 400 }}>
                                                                        →
                                                                    </Typography>
                                                                    <Box sx={{ textAlign: 'center' }}>
                                                                        {(() => {
                                                                            // Find the raw material for this ingredient
                                                                            const batchIngredient = batchIngredients.find(bi => bi.id === ingredient.batch_ingredient_id);
                                                                            const rawMaterial = batchIngredient?.raw_materials;
                                                                            
                                                                            if (ingredient.actual_quantity && rawMaterial) {
                                                                                const rawMaterialConversion = calculateRawMaterialAmountNeeded(
                                                                                    ingredient.actual_quantity,
                                                                                    ingredient.unit,
                                                                                    rawMaterial
                                                                                );
                                                                                
                                                                                if (rawMaterialConversion) {
                                                                                    return (
                                                                                        <>
                                                                                            <Typography variant="h6" sx={{ 
                                                                                                fontWeight: 500, 
                                                                                                color: theme.palette.success.main,
                                                                                                lineHeight: 1
                                                                                            }}>
                                                                                                {rawMaterialConversion.amount} {rawMaterialConversion.unit}
                                                                                            </Typography>
                                                                                            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 500, display: 'block' }}>
                                                                                                (≈{ingredient.actual_quantity} {ingredient.unit})
                                                                                            </Typography>
                                                                                        </>
                                                                                    );
                                                                                }
                                                                            }
                                                                            
                                                                            return (
                                                                                <Typography variant="h6" sx={{ 
                                                                                    fontWeight: 500, 
                                                                                    color: ingredient.actual_quantity ? theme.palette.success.main : theme.palette.text.secondary,
                                                                                    lineHeight: 1
                                                                                }}>
                                                                                    {ingredient.actual_quantity || '—'}
                                                                                </Typography>
                                                                            );
                                                                        })()}
                                                                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontWeight: 500 }}>
                                                                            ACTUAL
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography variant="body1" sx={{ 
                                                                        color: theme.palette.primary.main, 
                                                                        fontWeight: 500,
                                                                        backgroundColor: alpha(theme.palette.grey[300], 0.8),
                                                                        px: 2,
                                                                        py: 0.5,
                                                                        borderRadius: 2
                                                                    }}>
                                                                        {ingredient.unit?.toUpperCase()}
                                                                    </Typography>
                                                                </Box>

                                                                {ingredient.actual_quantity && ingredient.planned_quantity && (
                                                                    <Chip 
                                                                        label={`${variance > 0 ? '+' : ''}${variance.toFixed(1)}%`}
                                                                        color={isOutOfSpec ? 'error' : 'success'}
                                                                        sx={{ 
                                                                            fontWeight: 500,
                                                                            fontSize: '0.875rem',
                                                                            height: 32
                                                                        }}
                                                                    />
                                                                )}

                                                                {ingredient.added_by_operator && (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontWeight: 500 }}>
                                                                            OPERATOR:
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                                                            {ingredient.added_by_operator}
                                                                        </Typography>
                                                                    </Box>
                                                                )}

                                                                {ingredient.time_of_addition && (
                                                                    <Typography variant="body2" sx={{ 
                                                                        color: theme.palette.text.secondary,
                                                                        backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                                                        px: 2,
                                                                        py: 0.5,
                                                                        borderRadius: 2,
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {dayjs(ingredient.time_of_addition).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </CardContent>

                                                    {/* Expandable Details */}
                                                    <Collapse in={isExpanded}>
                                                        <CardContent sx={{ 
                                                            p: 4, 
                                                            backgroundColor: alpha(theme.palette.grey[50], 0.8),
                                                            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.06)}`
                                                            }
                                                        }}>
                                                            <Grid container spacing={3}>
                                                                {/* Process Parameters */}
                                                                {(ingredient.mixer_speed_rpm || ingredient.heating_temperature || ingredient.mixing_duration_minutes || ingredient.mixer_setting) && (
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
                                                                            Process Parameters
                                                                        </Typography>
                                                                        <Grid container spacing={2}>
                                                                            {ingredient.mixer_speed_rpm && (
                                                                                <Grid item xs={6} md={3}>
                                                                                    <Card sx={{ 
                                                                                        p: 2, 
                                                                                        textAlign: 'center', 
                                                                                        backgroundColor: 'white', 
                                                                                        border: `1px solid ${theme.palette.grey[400]}`,
                                                                                        borderRadius: 3,
                                                                                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                                        '&:hover': {
                                                                                            transform: 'translateY(-2px)',
                                                                                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                                                                        }
                                                                                    }}>
                                                                                        <SpeedIcon sx={{ fontSize: 24, color: theme.palette.primary.main, mb: 1 }} />
                                                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                                            {ingredient.mixer_speed_rpm}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                                            RPM
                                                                                        </Typography>
                                                                                    </Card>
                                                                                </Grid>
                                                                            )}
                                                                            {ingredient.heating_temperature && (
                                                                                <Grid item xs={6} md={3}>
                                                                                    <Card sx={{ 
                                                                                        p: 2, 
                                                                                        textAlign: 'center', 
                                                                                        backgroundColor: 'white', 
                                                                                        border: `1px solid ${theme.palette.grey[400]}`,
                                                                                        borderRadius: 3,
                                                                                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                                        '&:hover': {
                                                                                            transform: 'translateY(-2px)',
                                                                                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                                                                        }
                                                                                    }}>
                                                                                        <ThermostatIcon sx={{ fontSize: 24, color: theme.palette.primary.main, mb: 1 }} />
                                                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                                            {formatTemperatureDisplay(ingredient.heating_temperature, 'F')}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                                            TEMPERATURE
                                                                                        </Typography>
                                                                                    </Card>
                                                                                </Grid>
                                                                            )}
                                                                            {ingredient.mixing_duration_minutes && (
                                                                                <Grid item xs={6} md={3}>
                                                                                    <Card sx={{ 
                                                                                        p: 2, 
                                                                                        textAlign: 'center', 
                                                                                        backgroundColor: 'white', 
                                                                                        border: `1px solid ${theme.palette.grey[400]}`,
                                                                                        borderRadius: 3,
                                                                                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                                        '&:hover': {
                                                                                            transform: 'translateY(-2px)',
                                                                                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                                                                        }
                                                                                    }}>
                                                                                        <ScheduleIcon sx={{ fontSize: 24, color: theme.palette.primary.main, mb: 1 }} />
                                                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                                            {ingredient.mixing_duration_minutes}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                                            MINUTES
                                                                                        </Typography>
                                                                                    </Card>
                                                                                </Grid>
                                                                            )}
                                                                            {ingredient.mixer_setting && (
                                                                                <Grid item xs={6} md={3}>
                                                                                    <Card sx={{ 
                                                                                        p: 2, 
                                                                                        textAlign: 'center', 
                                                                                        backgroundColor: 'white', 
                                                                                        border: `1px solid ${theme.palette.grey[400]}`,
                                                                                        borderRadius: 3,
                                                                                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                                        '&:hover': {
                                                                                            transform: 'translateY(-2px)',
                                                                                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                                                                        }
                                                                                    }}>
                                                                                        <SettingsIcon sx={{ fontSize: 24, color: theme.palette.primary.main, mb: 1 }} />
                                                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                                            {ingredient.mixer_setting}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                                                                            MIXER SETTING
                                                                                        </Typography>
                                                                                    </Card>
                                                                                </Grid>
                                                                            )}
                                                                        </Grid>
                                                                    </Grid>
                                                                )}

                                                                {/* Batch Information */}
                                                {(batchNumber || ingredient.id) && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
                                                            Batch Information
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            {/* Multiple Lots Display */}
                                                            <IngredientLotsCards 
                                                                ingredient={ingredient}
                                                                lotNumber={lotNumber}
                                                                theme={theme}
                                                                alpha={alpha}
                                                                batchIngredients={batchIngredients}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                                                                                {/* Quality Control */}
                                                {(ingredient.ph_reading || ingredient.visual_assessment || ingredient.viscosity_reading) && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
                                                            Quality Control
                                                        </Typography>
                                                        <Grid container spacing={2} sx={{ 
                                                            display: 'flex', 
                                                            justifyContent: 'flex-start',
                                                            '& .MuiGrid-item': {
                                                                display: 'flex',
                                                                '& .MuiCard-root': {
                                                                    width: '100%'
                                                                }
                                                            }
                                                        }}>

                                                            {/* pH Reading */}
                                                            {ingredient.ph_reading && (
                                                                <Grid item xs={6} sm={4} lg={3}>
                                                                    <Card sx={{ 
                                                                        p: 2, 
                                                                        textAlign: 'center', 
                                                                        backgroundColor: alpha(theme.palette.info.main, 0.1), 
                                                                        border: `1px solid ${theme.palette.info.main}`,
                                                                        borderRadius: 3,
                                                                        boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.08)}`,
                                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                        '&:hover': {
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: `0 8px 32px ${alpha(theme.palette.info.main, 0.12)}`
                                                                        }
                                                                    }}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.info.main }}>
                                                                            {ingredient.ph_reading}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                            pH LEVEL
                                                                        </Typography>
                                                                    </Card>
                                                                </Grid>
                                                            )}
                                                            
                                                            {/* Viscosity Reading */}
                                                            {ingredient.viscosity_reading && (
                                                                <Grid item xs={6} sm={4} lg={3}>
                                                                    <Card sx={{ 
                                                                        p: 2, 
                                                                        textAlign: 'center', 
                                                                        backgroundColor: alpha(theme.palette.warning.main, 0.1), 
                                                                        border: `1px solid ${theme.palette.warning.main}`,
                                                                        borderRadius: 3,
                                                                        boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.08)}`,
                                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                        '&:hover': {
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`
                                                                        }
                                                                    }}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.warning.main }}>
                                                                            {ingredient.viscosity_reading}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                            VISCOSITY
                                                                        </Typography>
                                                                    </Card>
                                                                </Grid>
                                                            )}
                                                            
                                                            {/* Visual Assessment */}
                                                            {ingredient.visual_assessment && (
                                                                <Grid item xs={6} sm={4} lg={3}>
                                                                    <Card sx={{ 
                                                                        p: 2, 
                                                                        textAlign: 'center', 
                                                                        backgroundColor: ingredient.visual_assessment === 'Good' ? 
                                                                            alpha(theme.palette.success.main, 0.1) : 
                                                                            ingredient.visual_assessment === 'Acceptable' ? 
                                                                            alpha(theme.palette.warning.main, 0.1) : 
                                                                            alpha(theme.palette.error.main, 0.1),
                                                                        border: `1px solid ${ingredient.visual_assessment === 'Good' ? 
                                                                            theme.palette.success.main : 
                                                                            ingredient.visual_assessment === 'Acceptable' ? 
                                                                            theme.palette.warning.main : 
                                                                            theme.palette.error.main}`,
                                                                        borderRadius: 3,
                                                                        boxShadow: `0 4px 20px ${alpha(
                                                                            ingredient.visual_assessment === 'Good' ? 
                                                                            theme.palette.success.main : 
                                                                            ingredient.visual_assessment === 'Acceptable' ? 
                                                                            theme.palette.warning.main : 
                                                                            theme.palette.error.main, 0.08)}`,
                                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                        '&:hover': {
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: `0 8px 32px ${alpha(
                                                                                ingredient.visual_assessment === 'Good' ? 
                                                                                theme.palette.success.main : 
                                                                                ingredient.visual_assessment === 'Acceptable' ? 
                                                                                theme.palette.warning.main : 
                                                                                theme.palette.error.main, 0.12)}`
                                                                        }
                                                                    }}>
                                                                        <Typography variant="h6" sx={{ 
                                                                            fontWeight: 500, 
                                                                            color: ingredient.visual_assessment === 'Good' ? 
                                                                                theme.palette.success.main : 
                                                                                ingredient.visual_assessment === 'Acceptable' ? 
                                                                                theme.palette.warning.main : 
                                                                                theme.palette.error.main
                                                                        }}>
                                                                            {ingredient.visual_assessment}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                            VISUAL CHECK
                                                                        </Typography>
                                                                    </Card>
                                                                </Grid>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                                {/* Operator & Timing */}
                                                                {ingredient.added_by_operator && (
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
                                                                            Operator & Timing
                                                                        </Typography>
                                                                        <Card sx={{ 
                                                            p: 3, 
                                                            backgroundColor: 'white', 
                                                            border: `1px solid ${theme.palette.grey[400]}`,
                                                            borderRadius: 3,
                                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                                                            }
                                                        }}>
                                                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.primary }}>
                                                                Operator: {ingredient.added_by_operator}
                                                            </Typography>
                                                            {ingredient.time_of_addition && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                                    Added: {dayjs(ingredient.time_of_addition).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                                                </Typography>
                                                            )}
                                                        </Card>
                                                                    </Grid>
                                                                )}

                                                                {/* Notes */}
                                                                {ingredient.notes && (
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: theme.palette.text.primary, fontSize: '1.2rem' }}>
                                                                            Notes
                                                                        </Typography>
                                                                        <Card sx={{ 
                                                            p: 3, 
                                                            backgroundColor: alpha(theme.palette.warning.main, 0.05), 
                                                            border: `1px solid ${theme.palette.warning.main}`,
                                                            borderRadius: 3,
                                                            boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.08)}`,
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`
                                                            }
                                                        }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                {ingredient.notes}
                                                            </Typography>
                                                        </Card>
                                                                    </Grid>
                                                                )}
                                                            </Grid>
                                                        </CardContent>
                                                    </Collapse>
                                                </Card>
                                            );
                                        })}
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteConfirmOpen} 
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                        maxHeight: '90vh',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: theme.palette.error.main,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: alpha(theme.palette.error.main, 0.05),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 3
                }}>
                    Confirm Delete Ingredient
                </DialogTitle>
                <DialogContent sx={{ 
                    backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                    p: 3,
                    backdropFilter: 'blur(10px)',
                    mt: 3
                }}>
                    <Typography variant="body1" sx={{ mb: 2, mt: 2, fontWeight: 500 }}>
                        Are you sure you want to delete this ingredient from the mixing timeline?
                    </Typography>
                    {ingredientToDelete && (
                        <Box sx={{ 
                            p: 3, 
                            backgroundColor: alpha(theme.palette.grey[100], 0.8), 
                            borderRadius: 3, 
                            border: `1px solid ${theme.palette.grey[400]}`, 
                            mb: 3
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                {ingredientToDelete.ingredient_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Step {ingredientToDelete.addition_order}
                                {ingredientToDelete.actual_quantity && ` • ${ingredientToDelete.actual_quantity} ${ingredientToDelete.unit}`}
                                {ingredientToDelete.lot_number && ` • LOT: ${ingredientToDelete.lot_number}`}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    gap: 2, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                }}>
                    <Button 
                        onClick={() => setDeleteConfirmOpen(false)}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.04)
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmDelete}
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        disabled={!canDelete}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            background: canDelete ? `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})` : theme.palette.grey[400],
                            color: canDelete ? 'white' : theme.palette.grey[100],
                            fontWeight: 500,
                            boxShadow: canDelete ? `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}` : 'none',
                            '&:hover': {
                                background: canDelete ? `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})` : theme.palette.grey[400],
                                boxShadow: canDelete ? `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}` : 'none'
                            },
                            '&:disabled': {
                                background: theme.palette.grey[400],
                                color: theme.palette.grey[100]
                            }
                        }}
                    >
                        {canDelete ? 'Delete Ingredient' : 'Read Only - No Delete Permissions'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Ingredient Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                        maxHeight: '90vh',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: 'primary.main',
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: alpha(theme.palette.primary.main, 0.05),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 3
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        {editingIngredient ? 'Edit Ingredient Log' : 'Add Ingredient Log'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ 
                    backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                    p: 3,
                    maxHeight: '80vh', 
                    overflowY: 'auto',
                    backdropFilter: 'blur(10px)',
                    mt: 3
                }}>
                    <Grid container spacing={3}>
                        {/* BATCH INGREDIENT SELECTION */}
                        {!editingIngredient && availableBatchIngredients.length > 0 && (
                            <Grid item xs={12} sx={{ mt: 3 }}>
                                <Card sx={{ 
                                    p: 3, 
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 3, 
                                    backgroundColor: 'white',
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                        Select Batch Ingredient
                                    </Typography>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => {
                                            handleOpenBatchIngredientSelection();
                                        }}
                                        disabled={!canEdit}
                                        sx={{
                                            height: 56,
                                            borderRadius: 3,
                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                            background: 'white',
                                            justifyContent: 'flex-start',
                                            px: 3,
                                            py: 2.5,
                                            '&:hover': {
                                                border: canEdit ? `1px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                                boxShadow: canEdit ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` : 'none'
                                            },
                                            '&:disabled': {
                                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                                background: alpha(theme.palette.grey[300], 0.5),
                                                color: theme.palette.grey[500]
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <ScienceIcon sx={{ mr: 2, color: 'text.secondary' }} />
                                            <Box sx={{ textAlign: 'left', flex: 1 }}>
                                                {formData.batch_ingredient_id ? (
                                                    <>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                            {formData.ingredient_name}
                                                        </Typography>
                                                        {formData.supplier_name && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formData.supplier_name}
                                                                {formData.lot_number && ` • LOT: ${formData.lot_number}`}
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                                                        {canEdit ? 'Choose from existing batch ingredients' : 'Read-only - no edit permissions'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Button>

                                </Card>
                            </Grid>
                        )}

                        {/* CONVERSION WARNING */}
                        {unitConversionWarning && (
                            <Grid item xs={12}>
                                <Alert 
                                    severity={unitConversionWarning.includes('Warning') ? 'warning' : 'info'} 
                                    icon={<ConvertIcon />} 
                                    sx={{ 
                                        mb: 2,
                                        borderRadius: 2,
                                        backgroundColor: alpha(unitConversionWarning.includes('Warning') ? theme.palette.warning.main : theme.palette.info.main, 0.1),
                                        border: `1px solid ${alpha(unitConversionWarning.includes('Warning') ? theme.palette.warning.main : theme.palette.info.main, 0.3)}`,
                                        '& .MuiAlert-icon': {
                                            color: unitConversionWarning.includes('Warning') ? theme.palette.warning.main : theme.palette.info.main
                                        }
                                    }}
                                >
                                    {unitConversionWarning}
                                </Alert>
                            </Grid>
                        )}

                        {/* INGREDIENT DETAILS */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                                }
                            }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                        Ingredient Details
                                    </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Ingredient Name"
                                            value={formData.ingredient_name}
                                            onChange={(e) => setFormData({...formData, ingredient_name: e.target.value})}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Supplier"
                                            value={formData.supplier_name}
                                            onChange={(e) => setFormData({...formData, supplier_name: e.target.value})}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                            placeholder="Supplier name"
                                        />
                                    </Grid>
                                </Grid>

                                {/* Multiple Lots Section */}
                                {ingredientLots.length > 0 && (
                                    <>
                                        <Typography variant="h6" sx={{ fontWeight: 500, mt: 3, mb: 2, color: theme.palette.text.primary }}>
                                            Lot/Batch Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {ingredientLots.map((lot, index) => (
                                                <Grid item xs={12} sm={6} md={4} key={lot.id}>
                                                    <Card sx={{
                                                        p: 2,
                                                        border: `1px solid ${theme.palette.grey[300]}`,
                                                        borderRadius: 2,
                                                        backgroundColor: alpha(theme.palette.success.main, 0.05),
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`
                                                        }
                                                    }}>
                                                        <Typography variant="subtitle2" sx={{ 
                                                            fontWeight: 500, 
                                                            color: theme.palette.success.main,
                                                            mb: 1
                                                        }}>
                                                            {lot.lot_number ? `Lot ${lot.lot_number}` : `Batch ${lot.batch_number}`}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ 
                                                            color: theme.palette.text.secondary,
                                                            mb: 0.5
                                                        }}>
                                                            Quantity: {lot.quantity_used} {(() => {
                                                                // Get the raw material unit from the batch ingredient
                                                                const batchIngredient = batchIngredients.find(bi => bi.id === formData.batch_ingredient_id);
                                                                const rawMaterialUnit = batchIngredient?.raw_materials?.primary_unit_measure;
                                                                return rawMaterialUnit || lot.unit_used || 'units';
                                                            })()}
                                                        </Typography>
                                                        {lot.location_name && (
                                                            <Typography variant="caption" sx={{ 
                                                                color: theme.palette.text.secondary,
                                                                display: 'block'
                                                            }}>
                                                                Location: {lot.location_name}
                                                            </Typography>
                                                        )}
                                                        {lot.expiry_date && (
                                                            <Typography variant="caption" sx={{ 
                                                                color: theme.palette.text.secondary,
                                                                display: 'block'
                                                            }}>
                                                                Expires: {new Date(lot.expiry_date).toLocaleDateString()}
                                                            </Typography>
                                                        )}
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </>
                                )}
                            </Card>
                        </Grid>

                        {/* QUANTITIES */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Quantities
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Planned Quantity"
                                            type="number"
                                            value={formData.planned_quantity}
                                            onChange={(e) => setFormData({...formData, planned_quantity: e.target.value})}
                                            inputProps={{ step: 0.001 }}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Actual Quantity"
                                            type="number"
                                            value={formData.actual_quantity}
                                            onChange={(e) => setFormData({...formData, actual_quantity: e.target.value})}
                                            inputProps={{ step: 0.001 }}
                                            required
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Unit"
                                            value={formData.unit}
                                            onChange={(e) => handleUnitChange(e.target.value)}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        >
                                            {getAllUnits().map(unit => (
                                                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>

                        {/* TIMING & OPERATOR */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Timing & Operator
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <DateTimePicker
                                        label="Time of Addition"
                                        value={formData.time_of_addition}
                                        onChange={(newValue) => setFormData({...formData, time_of_addition: newValue})}
                                        format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                                        ampm={batchTimeFormat !== '24hour'}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                disabled: !canEdit || !formData.batch_ingredient_id
                                            }
                                        }}
                                        disabled={!canEdit || !formData.batch_ingredient_id}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Added by Operator"
                                        value={formData.added_by_operator}
                                        onChange={(e) => setFormData({...formData, added_by_operator: e.target.value})}
                                        disabled={!canEdit || !formData.batch_ingredient_id}
                                    />
                                </Box>
                            </Card>
                        </Grid>

                        {/* MIXER SETTINGS */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Mixer Settings
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Mixer Speed (RPM)"
                                            type="number"
                                            value={formData.mixer_speed_rpm}
                                            onChange={(e) => setFormData({...formData, mixer_speed_rpm: e.target.value})}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Mixer Setting"
                                            value={formData.mixer_setting}
                                            onChange={(e) => setFormData({...formData, mixer_setting: e.target.value})}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Heating Temperature"
                                            type="number"
                                            value={formData.heating_temperature}
                                            onChange={(e) => setFormData({...formData, heating_temperature: e.target.value})}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        °{formData.heating_temperature_unit}
                                                    </InputAdornment>
                                                ),
                                            }}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Temperature Unit"
                                            value={formData.heating_temperature_unit}
                                            onChange={(e) => handleTemperatureUnitChange(e.target.value)}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        >
                                            <MenuItem value="F">Fahrenheit (°F)</MenuItem>
                                            <MenuItem value="C">Celsius (°C)</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Duration (min)"
                                            type="number"
                                            value={formData.mixing_duration_minutes}
                                            onChange={(e) => setFormData({...formData, mixing_duration_minutes: e.target.value})}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        />
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>

                        {/* QUALITY CONTROL */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Quality Control Checks
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="pH Reading"
                                            type="number"
                                            step="0.1"
                                            value={formData.ph_reading}
                                            onChange={(e) => setFormData({...formData, ph_reading: e.target.value})}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Viscosity Reading"
                                            type="number"
                                            value={formData.viscosity_reading}
                                            onChange={(e) => setFormData({...formData, viscosity_reading: e.target.value})}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Visual Assessment"
                                            value={formData.visual_assessment}
                                            onChange={(e) => setFormData({...formData, visual_assessment: e.target.value})}
                                            disabled={!canEdit || !formData.batch_ingredient_id}
                                        >
                                            <MenuItem value="Good">Good</MenuItem>
                                            <MenuItem value="Acceptable">Acceptable</MenuItem>
                                            <MenuItem value="Poor">Poor</MenuItem>
                                            <MenuItem value="Needs Review">Needs Review</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>

                        {/* NOTES */}
                        <Grid item xs={12}>
                            <Card sx={{ 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: 'white',
                                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                }
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                    Additional Notes
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Usage Notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                    placeholder={canEdit ? "Enter any notes about ingredient usage, quality, special handling, observations, or deviations..." : "Usage notes (read-only)"}
                                    disabled={!canEdit || !formData.batch_ingredient_id}
                                />
                            </Card>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    gap: 2, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                }}>
                    <Button onClick={() => {
                        setOpenDialog(false);
                        setUnitConversionWarning('');
                    }} 
                    startIcon={<CancelIcon />}
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                        }
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveIngredient} 
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={!canEdit || !formData.actual_quantity}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` : theme.palette.grey[400],
                            color: canEdit ? 'white' : theme.palette.grey[100],
                            fontWeight: 500,
                            boxShadow: canEdit ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                            '&:hover': {
                                background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` : theme.palette.grey[400],
                                boxShadow: canEdit ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}` : 'none'
                            },
                            '&:disabled': {
                                background: theme.palette.grey[400],
                                color: theme.palette.grey[100]
                            }
                        }}
                    >
                        {canEdit ? 'Save Ingredient' : 'Read Only - No Edit Permissions'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Batch Ingredient Selection Dialog */}
            <Dialog
                open={batchIngredientSelectionDialogOpen}
                onClose={handleCloseBatchIngredientSelection}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: 'primary.main',
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: alpha(theme.palette.primary.main, 0.05),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScienceIcon sx={{ mr: 2 }} />
                        Select Batch Ingredient
                    </Box>
                    <IconButton 
                        onClick={handleCloseBatchIngredientSelection} 
                        size="small"
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            '&:hover': {
                                transform: 'scale(1.1)',
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                        <TextField
                            fullWidth
                            placeholder="Search ingredients by name, lot number, supplier, or notes..."
                            value={batchIngredientSearchTerm}
                            onChange={(e) => setBatchIngredientSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    background: 'white',
                                    fontSize: '0.9rem',
                                    '& fieldset': { border: 'none' },
                                    '&:hover': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                    },
                                    '&.Mui-focused': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                    }
                                }
                            }}
                        />
                    </Box>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                    }}>
                                        Ingredient
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                    }}>
                                        Supplier & Lot
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                    }}>
                                        Quantity
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        width: 100
                                    }}>
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getFilteredBatchIngredients().length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography color="text.secondary">
                                                {batchIngredientSearchTerm ? 'No batch ingredients found matching your search.' : 'No batch ingredients available.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    getFilteredBatchIngredients().map((batchIngredient) => (
                                        <TableRow
                                            key={batchIngredient.id}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                                }
                                            }}
                                            onClick={() => handleSelectBatchIngredient(batchIngredient)}
                                        >
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                        {batchIngredient.raw_materials?.name || 'Unknown'}
                                                    </Typography>
                                                    {batchIngredient.usage_notes && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {batchIngredient.usage_notes}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    {batchIngredient.raw_materials?.supplier?.name && (
                                                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                            {batchIngredient.raw_materials.supplier.name}
                                                        </Typography>
                                                    )}
                                                    {(batchIngredient.supplier_lot_number || batchIngredient.supplier_batch_number) && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            LOT: {batchIngredient.supplier_lot_number || batchIngredient.supplier_batch_number}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    {formatUnitDisplay(batchIngredient.planned_amount, batchIngredient.planned_unit)}
                                                    {batchIngredient.actual_amount && batchIngredient.actual_amount !== batchIngredient.planned_amount && (
                                                        <>
                                                            <br />
                                                            <span style={{ color: theme.palette.success.main }}>
                                                                Actual: {formatUnitDisplay(batchIngredient.actual_amount, batchIngredient.actual_unit || batchIngredient.planned_unit)}
                                                            </span>
                                                        </>
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectBatchIngredient(batchIngredient);
                                                    }}
                                                    sx={{
                                                        borderRadius: 2,
                                                        fontWeight: 500,
                                                        fontSize: '0.8rem',
                                                        px: 2,
                                                        py: 0.5,
                                                        '&:hover': {
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                                        }
                                                    }}
                                                >
                                                    Select
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                    <Button 
                        onClick={handleCloseBatchIngredientSelection}
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            fontWeight: 500,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                            }
                        }}
                    >
                        Cancel
                    </Button>

                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
} 